import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs';
import { HydraCollection } from '../hydra/hydra-collection';
import { OrganisationProfileModel } from '../models/organisation/organisation-profile';
import { map} from 'rxjs/operators';
import { OrganisationModel } from '../models/organisation/organisation.model';
import { checkVAT, countries } from 'jsvat-be-fix';
import { ResourceCollectionFactory } from '../factory/resource-collection-factory';
import { TeacherSearchResult } from '../models/teacher-search';


@Injectable({
  providedIn: 'root'
})
export class OrganisationProfileApiService extends ApiService {
  readonly resourcePath = 'organisation_profiles';

  list(filters: Record<string, string> = {}): Observable<HydraCollection<OrganisationProfileModel>> {
    return this.restList<OrganisationProfileModel>(filters);
  }

  get(id: string): Observable<OrganisationProfileModel> {
    return this.restRead<OrganisationProfileModel>(id);
  }

  update(id: string, data: object): Observable<OrganisationProfileModel> {
    return this.restUpdate<OrganisationProfileModel>(id, data);
  }

  search(keyword:string, requireVat:boolean = false): Observable<HydraCollection<OrganisationProfileModel>>{
    var vat = checkVAT(keyword,countries);
    let url = this.resourceUrl + "/lookup?or[name]="+encodeURIComponent(keyword)+"&or[vatNumber]="+vat.value;
    if(requireVat){
      url += "&exists[vatNumber]=true";
    }
    return this.http.get<HydraCollection<OrganisationProfileModel>>(url).pipe(
      map(response => new HydraCollection<OrganisationProfileModel>(response))
    );
  }

  getMembers(id:string, params: Record<string, any>):Observable<any>{
    return this.http.get<HydraCollection<any>>(this.resourceUrl + "/"+id+"/members" + this.formatQueryString(params)).pipe(
      map(response => new HydraCollection<any>(response)),
      map(collection => ResourceCollectionFactory.createFromHydraCollection<any>(collection))
    );

  }

  getTrainingPlanCourses(id:string, params: Record<string, any>) {
    return this.http.get(this.resourceUrl + "/"+id+"/training_plan_courses" + this.formatQueryString(params))
  }

  teacherSearch(id:string, search:string):Observable<TeacherSearchResult>{
    return this.http.get<TeacherSearchResult>(this.resourceUrl + "/"+id+"/teacher_search" + this.formatQueryString({search}));
  }

  getCertifications(id:string, params: Record<string, any>) {
    return this.http.get(this.resourceUrl + "/"+id+"/default_certifications" + this.formatQueryString(params)).pipe(
      map(response => new HydraCollection<any>(response)),
      map(collection => ResourceCollectionFactory.createFromHydraCollection<any>(collection))
    )
  }

  getFunctions(id:string, params: Record<string, any> = {}): Observable<HydraCollection<any>> {
    return this.http.get<HydraCollection<any>>(this.resourceUrl + "/"+id+"/functions" + this.formatQueryString(params)).pipe(
      map(response => new HydraCollection<any>(response))
    );
  }

  getDepartments(id:string, params: Record<string, any> = {}): Observable<HydraCollection<any>> {
    return this.http.get<HydraCollection<any>>(this.resourceUrl + "/"+id+"/departments" + this.formatQueryString(params)).pipe(
      map(response => new HydraCollection<any>(response))
    );
  }

  getTrainingPlanRights(id:string, params: Record<string, any>) {
    return this.http.get(this.resourceUrl + "/"+id+"/training_plan" + this.formatQueryString(params))
  }
  getFinances(id:string, params: Record<string, any>) {
    return this.http.get(this.resourceUrl + "/"+id+"/training_plan/finance" + this.formatQueryString(params))
  }
  getTrainingPlanBudget(id:string, params: Record<string, any>) {
    return this.http.get(this.resourceUrl + "/"+id+"/training_plan/budgets" + this.formatQueryString(params))
  }
  getDashboard(id:string, params: Record<string, any>) {
    return this.http.get<any>(this.resourceUrl + "/"+id+"/dashboard" + this.formatQueryString(params))
  }

  getAdmins(id:string){
    return this.http.get<any>(this.apiUrl +'/me/organisationProfile/admins?orgProfile=' + id);
  }

  validateMembers(id:string, data:object){
    return this.http.post<any>(this.apiUrl +'/me/organisation_profiles/'+id+'/members/import/validate',data);
  }
  importMembers(id:string, data:object){
    return this.http.post<any>(this.apiUrl +'/me/organisation_profiles/'+id+'/members/import',data);
  }

  getOrganisations(id:string): Observable<HydraCollection<OrganisationModel>>{
    return this.http.get<HydraCollection<OrganisationModel>>(this.resourceUrl + "/"+ id+ "/organisations").pipe(
      map(response => new HydraCollection<OrganisationModel>(response))
    );
  }

  getExportList(id:string, params: Record<string, any>){
    return this.http.get(this.resourceUrl + "/"+id+"/training_plan/budgets" + this.formatQueryString(params))

  }

  exportPlan(id:string,query:string): Observable<any>{
    return this.http.post( this.resourceUrl + "/"+id+"/training_plan/export" + query ,{},{ responseType: 'blob' });    
  }

  generateFOD(id:string,query:string): Observable<any>{
    return this.http.post( this.resourceUrl + "/"+id+"/training_plan/export_fod" + query ,{},{ responseType: 'blob' });    
  }

  exportFinances(id:string,query:string): Observable<any>{
    return this.http.post( this.resourceUrl + "/"+id+"/training_plan/finance/export" + query ,{},{ responseType: 'blob' });
  }

  create(data:object){
      return this.restCreate<OrganisationProfileModel>(data);
  }
  
  delete(id:string){
    return this.restDelete(id);
  }

  
  getEnrollableCourse(id:string, courseId:string){
    return this.http.get(this.resourceUrl + '/'+id+'/enrollable_courses/'+courseId);
  }

  // The catalogues this member may request from: one entry per connected branch with a rule that
  // applies to them. The organisation's own plan is not in here.
  getEnrollableCourseProviders(id:string){
    return this.http.get<any[]>(this.resourceUrl + '/'+id+'/enrollable_course_providers');
  }

  getEnrollableCourses(id:string, params: Record<string, any> = {}){
    return this.http.get(this.resourceUrl + '/'+id+'/enrollable_courses' + this.formatQueryString(params));
  }
  
  updateSubscription(organisationProfileId:string, data:object){
    return this.http.patch<any>(this.resourceUrl+"/"+organisationProfileId+"/subscription/update",data);
  }

  checkUpdateSubscription(organisationProfileId:string, data:object){
    return this.http.patch<any>(this.resourceUrl+"/"+organisationProfileId+"/subscription/check",data);
  }

  // The modules sold on top of the package: the overview, and buying (dry run first), trying and
  // stopping one. Every POST names the add-on by the `id` the overview gave it.
  listSubscriptionAddOns(organisationProfileId:string){
    return this.http.get<{ addOns: any[] }>(this.resourceUrl+"/"+organisationProfileId+"/subscription/add_ons");
  }

  activateSubscriptionAddOn(organisationProfileId:string, addOnId:string, dryRun:boolean){
    return this.http.post<any>(this.resourceUrl+"/"+organisationProfileId+"/subscription/add_ons/activate", { addOnId, dryRun });
  }

  trialSubscriptionAddOn(organisationProfileId:string, addOnId:string){
    return this.http.post<any>(this.resourceUrl+"/"+organisationProfileId+"/subscription/add_ons/trial", { addOnId });
  }

  deactivateSubscriptionAddOn(organisationProfileId:string, addOnId:string){
    return this.http.post<any>(this.resourceUrl+"/"+organisationProfileId+"/subscription/add_ons/deactivate", { addOnId });
  }

  startTrialViaOnBoarding(organisationProfileId:string, data:object){
    return this.http.post<any>(this.resourceUrl+"/"+organisationProfileId+"/onboarding",data);
  }

  validateImportAttendees(id:string, data:object){
    return this.http.post<any>(this.apiUrl +'/me/organisation_profiles/'+id+'/courses/attendees/import/validate',data);
  }
  importAttendees(id:string, data:object){
    return this.http.post<any>(this.apiUrl +'/me/organisation_profiles/'+id+'/courses/attendees/import',data);
  }

  getAcademyConnections(id:string, activeOnly:boolean = false){
    return this.http.get(this.resourceUrl + '/'+id+'/branchconnections'+ (activeOnly?'/active':''));
  }

  saveAcademyConnections(id:string, data:object){
    return this.http.post<any>(this.resourceUrl + '/'+id+'/branchconnections',data);
  }
  
  getAcademyCourses(id:string, params: Record<string, any>){
    return this.http.get(this.resourceUrl + "/"+id+"/importable_series_for_me" + this.formatQueryString(params))
  }

  importAcademyCourses(id:string, data:object){
    return this.http.post<any>(this.resourceUrl + '/'+id+'/import_selected_courses',data);
  }

  getTrainingRightCompliance(id:string, params: Record<string, any>){
    return this.http.get(this.resourceUrl + "/"+id+"/training_rights_overview" + this.formatQueryString(params))
  }

  getSocialBalance(id: string, params: Record<string, any>) {
    return this.http.get<any>(this.resourceUrl + "/" + id + "/social_balance" + this.formatQueryString(params));
  }

  exportSocialBalance(id: string, params: Record<string, any>): Observable<any> {
    return this.http.post(this.resourceUrl + "/" + id + "/social_balance/export" + this.formatQueryString(params), {}, { responseType: 'blob' });
  }

  // The organisation's whole certification graph, flat and unpaginated, sorted by certification name.
  getCertificationNodes(id: string) {
    return this.http.get<any>(this.resourceUrl + "/" + id + "/certification_nodes");
  }

  // Every grant-type yearly maximum configured for the organisation.
  getGrantYearlyMaximums(id: string) {
    return this.http.get<any>(this.resourceUrl + "/" + id + "/grant_yearly_maximums");
  }

  // The full grant × joint-committee grid to edit for one year (rows the org may use, editable cells).
  getGrantYearlyMaximumMatrix(id: string, year: number) {
    return this.http.get<any>(this.resourceUrl + "/" + id + "/grant_yearly_maximum_matrix" + this.formatQueryString({ year }));
  }

  // Per-grant maximum vs. approved spend, one dataset per year in the range, grouped by
  // team | department | jointCommittee | year.
  getGrantAnalytics(id: string, params: Record<string, any>) {
    return this.http.get<any>(this.resourceUrl + "/" + id + "/grant_analytics" + this.formatQueryString(params));
  }

}
